<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { media } from '@ibgroup-tws-webapps/ratio.ui';
import PredictionMarketsIcon from '@/icons/PredictionMarketsIcon.vue';

withDefaults(defineProps<{ compact?: boolean }>(), { compact: false });
defineEmits<{ click: [event: MouseEvent] }>();

const { t } = useI18n();
const isDark = computed(() => media.scheme === 'dark');
</script>

<template>
  <button
    type="button"
    class="prediction-markets-banner group flex items-center text-left cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent motion-reduce:transform-none motion-reduce:transition-none"
    :class="[
      { 'prediction-markets-banner--dark': isDark, 'prediction-markets-banner--compact': compact },
      compact
        ? 'mx-2 my-1 min-h-8 w-[calc(100%-1rem)] gap-2 rounded-md px-2 py-1 hover:bg-accent-subtle'
        : 'mx-auto w-full max-w-xl gap-4 rounded-2xl px-5 py-4',
    ]"
    @click.stop="$emit('click', $event)"
  >
    <span
      v-if="!compact"
      class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-accent text-on-accent shadow-sm"
    >
      <PredictionMarketsIcon class="size-6" />
    </span>
    <span class="min-w-0" :class="{ 'flex-1': !compact }">
      <span
        class="block truncate"
        :class="
          compact
            ? 'whitespace-nowrap text-xs font-semibold leading-4 text-muted-color transition-colors group-hover:text-accent'
            : 'text-sm font-black leading-5 tracking-wide text-on-surface sm:text-base'
        "
      >
        {{ t('seeMoreAtPredictionMarkets') }}
      </span>
      <span v-if="!compact" class="mt-0.5 block text-xs leading-4 text-muted-color">
        {{ t('allCandidatesAndMarketDetails') }}
      </span>
    </span>
    <span
      aria-hidden="true"
      class="shrink-0 whitespace-nowrap font-bold transition-all duration-200 group-hover:translate-x-1 motion-reduce:transform-none"
      :class="compact ? 'text-sm text-muted-color group-hover:text-accent' : 'text-xl text-accent'"
      >&rarr;</span
    >
  </button>
</template>

<style scoped>
.prediction-markets-banner {
  border: 3px solid transparent;
  background:
    linear-gradient(var(--p-content-background), var(--p-content-background)) padding-box,
    linear-gradient(135deg, var(--color-republican-light), var(--color-democrat-light)) border-box;
  box-shadow:
    0 0 14px -3px color-mix(in srgb, var(--color-republican-light) 40%, transparent),
    0 0 14px -3px color-mix(in srgb, var(--color-democrat-light) 40%, transparent);
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.prediction-markets-banner--compact {
  border: 0;
  background: transparent;
  box-shadow: none;
}

.prediction-markets-banner--dark {
  background:
    linear-gradient(var(--p-content-background), var(--p-content-background)) padding-box,
    linear-gradient(135deg, var(--color-republican), var(--color-democrat)) border-box;
  box-shadow:
    0 0 14px -3px color-mix(in srgb, var(--color-republican) 40%, transparent),
    0 0 14px -3px color-mix(in srgb, var(--color-democrat) 40%, transparent);
}

.prediction-markets-banner:hover {
  box-shadow:
    0 0 20px -2px color-mix(in srgb, var(--color-republican-light) 50%, transparent),
    0 0 20px -2px color-mix(in srgb, var(--color-democrat-light) 50%, transparent);
  transform: translateY(-1px);
}

.prediction-markets-banner--dark:hover {
  box-shadow:
    0 0 20px -2px color-mix(in srgb, var(--color-republican) 50%, transparent),
    0 0 20px -2px color-mix(in srgb, var(--color-democrat) 50%, transparent);
}

.prediction-markets-banner--compact.prediction-markets-banner--dark {
  background: transparent;
  box-shadow: none;
}

.prediction-markets-banner--compact:hover,
.prediction-markets-banner--compact.prediction-markets-banner--dark:hover {
  background: var(--p-highlight-background);
  box-shadow: none;
  transform: none;
}
</style>
